// Admin-facing REST service for hallpass-backend's schools-api school
// configuration sub-resources. See src/api/config.js's SCHOOLS_API_BASE and
// src/api/http.js's schoolsApi().
//
// Every sub-resource router below is mounted UNDER the school router
// (apps/schools-api/src/app.ts):
//   schoolRouter.use("/:schoolId/schedule-types", scheduleTypeRouter)
//   scheduleTypeRouter.use("/:scheduleTypeId/periods", periodRouter)   (nested on scheduleTypeRouter itself)
//   schoolRouter.use("/:schoolId/calendar", calendarRouter)
//   schoolRouter.use("/:schoolId/destinations", destinationRouter)
//   schoolRouter.use("/:schoolId/class-sections", classSectionRouter)
//   schoolRouter.use("/:schoolId/policy", policyRouter)
//   schoolRouter.use("/:schoolId/schedule", scheduleRouter)
//   app.use("/api/schools", schoolRouter)
// so every handler reads req.params.schoolId — flat paths like
// /schedule-types/:id do NOT exist and would 404. Every mutation function
// below therefore takes schoolId (and scheduleTypeId, for period mutations)
// as an explicit parameter rather than assuming it's embedded in a
// sub-resource id.
//
// Role reference: the required role varies by endpoint and is documented per
// function below — it is not uniform. The /api/schools and /api/districts
// top-level collections are SUPER_ADMIN (getSchool also allows an ADMIN whose
// schoolId matches); the school-scoped sub-resource GETs require ADMIN-scoped
// requireSchoolAccess (and, for schedule-types/periods/calendar/destinations
// only, are also readable session-less via an API key — not used by this
// admin UI); the class-section roster GET additionally allows the section's
// own assigned teacher. Every POST/PATCH/PUT/DELETE requires ADMIN or
// SUPER_ADMIN (apps/schools-api/src/routes/*.ts). All of this is enforced
// server-side, not gated here.

import { schoolsApi, buildQuery } from './http';
import type { components } from './types.gen';

type SchoolCursorPage = components['schemas']['SchoolCursorPage'];
type SchoolResponse = components['schemas']['SchoolResponse'];
type CreateSchoolBody = components['schemas']['CreateSchoolBody'];
type UpdateSchoolBody = components['schemas']['UpdateSchoolBody'];
type DistrictCursorPage = components['schemas']['DistrictCursorPage'];
type DistrictResponse = components['schemas']['DistrictResponse'];
type CreateDistrictBody = components['schemas']['CreateDistrictBody'];
type UpdateDistrictBody = components['schemas']['UpdateDistrictBody'];
type ScheduleTypeListResponse =
  components['schemas']['ScheduleTypeListResponse'];
type ScheduleTypeResponse = components['schemas']['ScheduleTypeResponse'];
type CreateScheduleTypeBody = components['schemas']['CreateScheduleTypeBody'];
type UpdateScheduleTypeBody = components['schemas']['UpdateScheduleTypeBody'];
type SchoolPeriodListResponse =
  components['schemas']['SchoolPeriodListResponse'];
type SchoolPeriodResponse = components['schemas']['SchoolPeriodResponse'];
type CreateSchoolPeriodBody = components['schemas']['CreateSchoolPeriodBody'];
type UpdateSchoolPeriodBody = components['schemas']['UpdateSchoolPeriodBody'];
type PeriodListResponse = components['schemas']['PeriodListResponse'];
type PeriodResponse = components['schemas']['PeriodResponse'];
type CreatePeriodBody = components['schemas']['CreatePeriodBody'];
type UpdatePeriodBody = components['schemas']['UpdatePeriodBody'];
type CalendarEntryListResponse =
  components['schemas']['CalendarEntryListResponse'];
type CalendarBulkBody = components['schemas']['CalendarBulkBody'];
type SchoolCalendarResponse = components['schemas']['SchoolCalendarResponse'];
type UpdateCalendarEntryBody =
  components['schemas']['UpdateCalendarEntryBody'];
type BulkUpsertResult = components['schemas']['BulkUpsertResult'];
type DestinationListResponse =
  components['schemas']['DestinationListResponse'];
type DestinationResponse = components['schemas']['DestinationResponse'];
type CreateDestinationBody = components['schemas']['CreateDestinationBody'];
type UpdateDestinationBody = components['schemas']['UpdateDestinationBody'];
type PassPolicyResponse = components['schemas']['PassPolicyResponse'];
type UpsertPassPolicyBody = components['schemas']['UpsertPassPolicyBody'];
type ScheduleTodayResponse = components['schemas']['ScheduleTodayResponse'];
type ClassSectionListResponse =
  components['schemas']['ClassSectionListResponse'];
type ClassSectionResponse = components['schemas']['ClassSectionResponse'];
type CreateClassSectionBody = components['schemas']['CreateClassSectionBody'];
type UpdateClassSectionBody = components['schemas']['UpdateClassSectionBody'];
type ClassSectionRosterResponse =
  components['schemas']['ClassSectionRosterResponse'];
type PutClassSectionRosterBody =
  components['schemas']['PutClassSectionRosterBody'];
export type ClassSectionRosterConflictResponse =
  components['schemas']['ClassSectionRosterConflictResponse'];
type BulkClassSectionSyncBody =
  components['schemas']['BulkClassSectionSyncBody'];
type BulkClassSectionSyncResult =
  components['schemas']['BulkClassSectionSyncResult'];

/** A path resource id, accepted as either a number or its string form. */
type Id = number | string;

/**
 * Cursor-paginated list params shared by the /schools and /districts
 * collections. Declared as a `type` (not an `interface`) so it carries the
 * implicit index signature buildQuery's `Record<string, unknown>` requires.
 */
type CursorPageParams = {
  cursor?: string;
  limit?: number;
};

// ---------------------------------------------------------------------------
// Schools — /api/schools
// (apps/schools-api/src/routes/school.ts)
// ---------------------------------------------------------------------------

/**
 * SUPER_ADMIN only.
 *
 * @param params
 * @returns schools as {id, name, timezone, districtId, isPaid, createdAt, updatedAt}
 */
export function listSchools(
  params: CursorPageParams = {}
): Promise<SchoolCursorPage> {
  return schoolsApi<SchoolCursorPage>(`/api/schools${buildQuery(params)}`);
}

/**
 * SUPER_ADMIN, or an ADMIN whose schoolId matches id.
 *
 * @param id
 * @returns the school {id, name, timezone, districtId, isPaid, createdAt, updatedAt}
 */
export function getSchool(id: Id): Promise<SchoolResponse> {
  return schoolsApi<SchoolResponse>(`/api/schools/${id}`);
}

/**
 * SUPER_ADMIN only.
 *
 * @param body
 * @returns the created school {id, name, timezone, districtId, isPaid, createdAt, updatedAt}
 */
export function createSchool(body: CreateSchoolBody): Promise<SchoolResponse> {
  return schoolsApi<SchoolResponse>('/api/schools', {
    method: 'POST',
    body,
  });
}

/**
 * SUPER_ADMIN only.
 *
 * @param id
 * @param body
 * @returns the updated school {id, name, timezone, districtId, isPaid, createdAt, updatedAt}
 */
export function updateSchool(
  id: Id,
  body: UpdateSchoolBody
): Promise<SchoolResponse> {
  return schoolsApi<SchoolResponse>(`/api/schools/${id}`, {
    method: 'PATCH',
    body,
  });
}

/**
 * SUPER_ADMIN only. Soft-deletes the school.
 *
 * @param id
 * @returns null
 * @throws {import('./http').ApiError} 409 if the school has in-flight passes or active users
 */
export function deleteSchool(id: Id): Promise<null> {
  return schoolsApi<null>(`/api/schools/${id}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Districts — /api/districts
// (apps/schools-api/src/routes/district.ts)
// ---------------------------------------------------------------------------

/**
 * SUPER_ADMIN only. Note: the response has no "schools" field — a
 * district's schools are derived from School.districtId, not stored as an
 * array on the district row.
 *
 * @param params
 * @returns districts as {id, name, isPaid, createdAt, updatedAt}
 */
export function listDistricts(
  params: CursorPageParams = {}
): Promise<DistrictCursorPage> {
  return schoolsApi<DistrictCursorPage>(`/api/districts${buildQuery(params)}`);
}

/**
 * SUPER_ADMIN only.
 *
 * @param id
 * @returns the district {id, name, isPaid, createdAt, updatedAt}
 * @throws {import('./http').ApiError} 404 "District not found"
 */
export function getDistrict(id: Id): Promise<DistrictResponse> {
  return schoolsApi<DistrictResponse>(`/api/districts/${id}`);
}

/**
 * SUPER_ADMIN only.
 *
 * @param body
 * @returns the created district {id, name, isPaid, createdAt, updatedAt}
 */
export function createDistrict(
  body: CreateDistrictBody
): Promise<DistrictResponse> {
  return schoolsApi<DistrictResponse>('/api/districts', {
    method: 'POST',
    body,
  });
}

/**
 * SUPER_ADMIN only.
 *
 * @param id
 * @param body
 * @returns the updated district {id, name, isPaid, createdAt, updatedAt}
 */
export function updateDistrict(
  id: Id,
  body: UpdateDistrictBody
): Promise<DistrictResponse> {
  return schoolsApi<DistrictResponse>(`/api/districts/${id}`, {
    method: 'PATCH',
    body,
  });
}

/**
 * SUPER_ADMIN only. Soft-deletes the district.
 *
 * @param id
 * @returns null
 * @throws {import('./http').ApiError} 409 if the district still has active schools
 */
export function deleteDistrict(id: Id): Promise<null> {
  return schoolsApi<null>(`/api/districts/${id}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Schedule types — /api/schools/:schoolId/schedule-types
// (apps/schools-api/src/routes/scheduleType.ts)
// ---------------------------------------------------------------------------

/**
 * @param schoolId
 */
export function listScheduleTypes(
  schoolId: Id
): Promise<ScheduleTypeListResponse> {
  return schoolsApi<ScheduleTypeListResponse>(
    `/api/schools/${schoolId}/schedule-types`
  );
}

/**
 * @param schoolId
 * @param body
 * @returns the created schedule type
 */
export function createScheduleType(
  schoolId: Id,
  body: CreateScheduleTypeBody
): Promise<ScheduleTypeResponse> {
  return schoolsApi<ScheduleTypeResponse>(
    `/api/schools/${schoolId}/schedule-types`,
    {
      method: 'POST',
      body,
    }
  );
}

/**
 * @param schoolId
 * @param id
 * @param body
 * @returns the updated schedule type
 */
export function updateScheduleType(
  schoolId: Id,
  id: Id,
  body: UpdateScheduleTypeBody
): Promise<ScheduleTypeResponse> {
  return schoolsApi<ScheduleTypeResponse>(
    `/api/schools/${schoolId}/schedule-types/${id}`,
    {
      method: 'PATCH',
      body,
    }
  );
}

/**
 * @param schoolId
 * @param id
 * @returns null
 * @throws {import('./http').ApiError} 409 if referenced by calendar entries
 */
export function deleteScheduleType(schoolId: Id, id: Id): Promise<null> {
  return schoolsApi<null>(`/api/schools/${schoolId}/schedule-types/${id}`, {
    method: 'DELETE',
  });
}

// The replace-variant endpoint has no generated OpenAPI schema (it is not
// listed in types.gen.ts's operations), so its request/response are typed
// with narrow inline shapes here. The per-variant periods reuse the generated
// CreatePeriodBody, and the response rows reuse ScheduleTypeResponse.

/** One freshly-shaped ScheduleType in a replace-variant payload. */
interface ReplaceScheduleVariantNewType {
  abDay: 'A' | 'B' | null;
  periods: CreatePeriodBody[];
}

/** Request body for replaceScheduleVariant. */
interface ReplaceScheduleVariantBody {
  name: string;
  newScheduleTypes: ReplaceScheduleVariantNewType[];
  oldScheduleTypeIds: number[];
}

/** Response for replaceScheduleVariant. */
interface ReplaceScheduleVariantResponse {
  scheduleTypes: ScheduleTypeResponse[];
}

/**
 * Atomically replaces the ScheduleType(s) backing a schedule variant with a
 * freshly-shaped set (one for "single", two for "split" A/B), then retires
 * the old ScheduleType(s) — all in one server-side transaction.
 *
 * @param schoolId
 * @param body
 * @throws {import('./http').ApiError} 422 if a schoolPeriodId or an oldScheduleTypeIds entry is invalid; 409 on a name/period conflict or a calendar-referenced old schedule type
 */
export function replaceScheduleVariant(
  schoolId: Id,
  body: ReplaceScheduleVariantBody
): Promise<ReplaceScheduleVariantResponse> {
  return schoolsApi<ReplaceScheduleVariantResponse>(
    `/api/schools/${schoolId}/schedule-types/replace-variant`,
    {
      method: 'PUT',
      body,
    }
  );
}

// ---------------------------------------------------------------------------
// School periods — /api/schools/:schoolId/school-periods
// (apps/schools-api/src/routes/school-period.ts)
//
// The canonical, schedule-type-agnostic period identity for a school (e.g.
// "Period 4" always means the same academic period regardless of which
// ScheduleType/Period below is active that day) — see docs/SCHEMA_PLAN.md.
// ---------------------------------------------------------------------------

/**
 * @param schoolId
 * @returns ordered by displayOrder
 */
export function listSchoolPeriods(
  schoolId: Id
): Promise<SchoolPeriodListResponse> {
  return schoolsApi<SchoolPeriodListResponse>(
    `/api/schools/${schoolId}/school-periods`
  );
}

/**
 * @param schoolId
 * @param body
 * @returns the created school period
 * @throws {import('./http').ApiError} 409 if a period with that name already exists
 */
export function createSchoolPeriod(
  schoolId: Id,
  body: CreateSchoolPeriodBody
): Promise<SchoolPeriodResponse> {
  return schoolsApi<SchoolPeriodResponse>(
    `/api/schools/${schoolId}/school-periods`,
    {
      method: 'POST',
      body,
    }
  );
}

/**
 * @param schoolId
 * @param id
 * @param body
 * @returns the updated school period
 * @throws {import('./http').ApiError} 409 if renaming collides with another period's name
 */
export function updateSchoolPeriod(
  schoolId: Id,
  id: Id,
  body: UpdateSchoolPeriodBody
): Promise<SchoolPeriodResponse> {
  return schoolsApi<SchoolPeriodResponse>(
    `/api/schools/${schoolId}/school-periods/${id}`,
    {
      method: 'PATCH',
      body,
    }
  );
}

/**
 * @param schoolId
 * @param id
 * @returns null
 * @throws {import('./http').ApiError} 409 if referenced by a live Period or ClassSection
 */
export function deleteSchoolPeriod(schoolId: Id, id: Id): Promise<null> {
  return schoolsApi<null>(`/api/schools/${schoolId}/school-periods/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Next available displayOrder when adding a new SchoolPeriod after existing
 * ones -- one past the current maximum (1 if none exist yet, matching
 * seed.ts's canonicalPeriodDefs which numbers from 1). Ignores entries with
 * no numeric displayOrder (e.g. ScheduleVariantModal.js's in-memory-only
 * pending default periods, which use displayOrder: null until they're
 * actually persisted -- see resolveDefaultPeriods).
 * @param schoolPeriods
 */
export function nextDisplayOrder(
  schoolPeriods: { displayOrder: number | null }[]
): number {
  const orders = schoolPeriods
    .map((sp) => sp.displayOrder)
    .filter((d): d is number => typeof d === 'number');
  return Math.max(0, ...orders) + 1;
}

// ---------------------------------------------------------------------------
// Periods — /api/schools/:schoolId/schedule-types/:scheduleTypeId/periods
// (apps/schools-api/src/routes/period.ts)
// ---------------------------------------------------------------------------

/**
 * @param schoolId
 * @param scheduleTypeId
 */
export function listPeriods(
  schoolId: Id,
  scheduleTypeId: Id
): Promise<PeriodListResponse> {
  return schoolsApi<PeriodListResponse>(
    `/api/schools/${schoolId}/schedule-types/${scheduleTypeId}/periods`
  );
}

/**
 * @param schoolId
 * @param scheduleTypeId
 * @param body
 *   startTime/endTime are "HH:MM" strings; startTime must be before endTime.
 * @returns the created period
 * @throws {import('./http').ApiError} 422 if schoolPeriodId doesn't belong to this school; 409 if this schedule type already has a period for it
 */
export function createPeriod(
  schoolId: Id,
  scheduleTypeId: Id,
  body: CreatePeriodBody
): Promise<PeriodResponse> {
  return schoolsApi<PeriodResponse>(
    `/api/schools/${schoolId}/schedule-types/${scheduleTypeId}/periods`,
    { method: 'POST', body }
  );
}

/**
 * @param schoolId
 * @param scheduleTypeId
 * @param id
 * @param body
 * @returns the updated period
 * @throws {import('./http').ApiError} 422 if the merged start/end times are out of order, or schoolPeriodId doesn't belong to this school; 409 on a duplicate schoolPeriodId within the schedule type
 */
export function updatePeriod(
  schoolId: Id,
  scheduleTypeId: Id,
  id: Id,
  body: UpdatePeriodBody
): Promise<PeriodResponse> {
  return schoolsApi<PeriodResponse>(
    `/api/schools/${schoolId}/schedule-types/${scheduleTypeId}/periods/${id}`,
    { method: 'PATCH', body }
  );
}

/**
 * @param schoolId
 * @param scheduleTypeId
 * @param id
 * @returns null
 * @throws {import('./http').ApiError} 409 if the period has in-flight passes
 */
export function deletePeriod(
  schoolId: Id,
  scheduleTypeId: Id,
  id: Id
): Promise<null> {
  return schoolsApi<null>(
    `/api/schools/${schoolId}/schedule-types/${scheduleTypeId}/periods/${id}`,
    { method: 'DELETE' }
  );
}

// ---------------------------------------------------------------------------
// Calendar — /api/schools/:schoolId/calendar
// (apps/schools-api/src/routes/calendar.ts)
// ---------------------------------------------------------------------------

/**
 * YYYY-MM-DD date bounds for the calendar list query. Declared as a `type`
 * (not an `interface`) so it carries the implicit index signature
 * buildQuery's `Record<string, unknown>` requires.
 */
type ListCalendarParams = {
  from?: string;
  to?: string;
};

/**
 * @param schoolId
 * @param params YYYY-MM-DD date bounds
 */
export function listCalendar(
  schoolId: Id,
  params: ListCalendarParams = {}
): Promise<CalendarEntryListResponse> {
  return schoolsApi<CalendarEntryListResponse>(
    `/api/schools/${schoolId}/calendar${buildQuery(params)}`
  );
}

/**
 * Bulk upsert on (schoolId, date) — ON CONFLICT DO UPDATE server-side.
 * Accepts either a single entry object or an array (calendarBulkSchema,
 * packages/types/src/schemas.ts, max 366 entries when an array).
 *
 * @param schoolId
 * @param body
 * @throws {import('./http').ApiError} 422 on duplicate date in payload, or an unknown/deleted scheduleTypeId
 */
export function createCalendarEntries(
  schoolId: Id,
  body: CalendarBulkBody
): Promise<BulkUpsertResult> {
  return schoolsApi<BulkUpsertResult>(`/api/schools/${schoolId}/calendar`, {
    method: 'POST',
    body,
  });
}

/**
 * @param schoolId
 * @param id
 * @param body
 * @returns the updated calendar entry
 */
export function updateCalendarEntry(
  schoolId: Id,
  id: Id,
  body: UpdateCalendarEntryBody
): Promise<SchoolCalendarResponse> {
  return schoolsApi<SchoolCalendarResponse>(
    `/api/schools/${schoolId}/calendar/${id}`,
    {
      method: 'PATCH',
      body,
    }
  );
}

/**
 * @param schoolId
 * @param id
 * @returns null
 */
export function deleteCalendarEntry(schoolId: Id, id: Id): Promise<null> {
  return schoolsApi<null>(`/api/schools/${schoolId}/calendar/${id}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Destinations — /api/schools/:schoolId/destinations
// (apps/schools-api/src/routes/destination.ts)
// ---------------------------------------------------------------------------

/**
 * @param schoolId
 */
export function listDestinations(
  schoolId: Id
): Promise<DestinationListResponse> {
  return schoolsApi<DestinationListResponse>(
    `/api/schools/${schoolId}/destinations`
  );
}

/**
 * @param schoolId
 * @param body
 * @returns the created destination
 */
export function createDestination(
  schoolId: Id,
  body: CreateDestinationBody
): Promise<DestinationResponse> {
  return schoolsApi<DestinationResponse>(
    `/api/schools/${schoolId}/destinations`,
    {
      method: 'POST',
      body,
    }
  );
}

/**
 * @param schoolId
 * @param id
 * @param body
 * @returns the updated destination
 */
export function updateDestination(
  schoolId: Id,
  id: Id,
  body: UpdateDestinationBody
): Promise<DestinationResponse> {
  return schoolsApi<DestinationResponse>(
    `/api/schools/${schoolId}/destinations/${id}`,
    {
      method: 'PATCH',
      body,
    }
  );
}

/**
 * @param schoolId
 * @param id
 * @returns null
 * @throws {import('./http').ApiError} 409 if the destination has in-flight passes
 */
export function deleteDestination(schoolId: Id, id: Id): Promise<null> {
  return schoolsApi<null>(`/api/schools/${schoolId}/destinations/${id}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Policy — /api/schools/:schoolId/policy (singleton per school)
// (apps/schools-api/src/routes/policy.ts)
// ---------------------------------------------------------------------------

/**
 * @param schoolId
 * @returns {id, schoolId, maxActivePasses, interval, maxPerInterval}
 * @throws {import('./http').ApiError} 404 "No policy set for this school"
 */
export function getPolicy(schoolId: Id): Promise<PassPolicyResponse> {
  return schoolsApi<PassPolicyResponse>(`/api/schools/${schoolId}/policy`);
}

/**
 * Full-replace upsert — omitted fields are cleared to null, not left
 * unchanged (unlike the per-student PUT in passLimitsApi.js). `interval`
 * and `maxPerInterval` must be set together or both omitted.
 *
 * @param schoolId
 * @param body
 * @returns the upserted policy
 */
export function putPolicy(
  schoolId: Id,
  body: UpsertPassPolicyBody
): Promise<PassPolicyResponse> {
  return schoolsApi<PassPolicyResponse>(`/api/schools/${schoolId}/policy`, {
    method: 'PUT',
    body,
  });
}

// ---------------------------------------------------------------------------
// Today's schedule — /api/schools/:schoolId/schedule/today
// (apps/schools-api/src/routes/schedule.ts)
// ---------------------------------------------------------------------------

/**
 * Today's resolved schedule (in the school's timezone) and current period,
 * using the same resolver POST /api/passes uses. A day with no calendar
 * entry (or one with no schedule type) is a normal "no school today" —
 * returns 200 with scheduleType: null and an empty periods list, not a 404.
 *
 * @param schoolId
 * @returns {date, scheduleType, periods, currentPeriod}
 */
export function getTodaySchedule(schoolId: Id): Promise<ScheduleTodayResponse> {
  return schoolsApi<ScheduleTodayResponse>(
    `/api/schools/${schoolId}/schedule/today`
  );
}

// ---------------------------------------------------------------------------
// Class sections — /api/schools/:schoolId/class-sections
// (apps/schools-api/src/routes/class-section.ts)
// ---------------------------------------------------------------------------

/**
 * @param schoolId
 */
export function listClassSections(
  schoolId: Id
): Promise<ClassSectionListResponse> {
  return schoolsApi<ClassSectionListResponse>(
    `/api/schools/${schoolId}/class-sections`
  );
}

/**
 * Create a single class section. For bulk period-by-period sync, use
 * createClassSectionsBulk instead.
 *
 * @param schoolId
 * @param body
 * @returns the created class section
 * @throws {import('./http').ApiError} 422 if teacherId or schoolPeriodId doesn't belong to this school; 409 if this teacher already has a section for that school period
 */
export function createClassSection(
  schoolId: Id,
  body: CreateClassSectionBody
): Promise<ClassSectionResponse> {
  return schoolsApi<ClassSectionResponse>(
    `/api/schools/${schoolId}/class-sections`,
    {
      method: 'POST',
      body,
    }
  );
}

/**
 * Changing `schoolPeriodId` cascades to every enrollment in this section
 * server-side, so their denormalized schoolPeriodId stays in sync.
 *
 * @param schoolId
 * @param id
 * @param body
 * @returns the updated class section
 * @throws {import('./http').ApiError} 409 if the schoolPeriodId change conflicts with a student already enrolled elsewhere for that period
 */
export function updateClassSection(
  schoolId: Id,
  id: Id,
  body: UpdateClassSectionBody
): Promise<ClassSectionResponse> {
  return schoolsApi<ClassSectionResponse>(
    `/api/schools/${schoolId}/class-sections/${id}`,
    {
      method: 'PATCH',
      body,
    }
  );
}

/**
 * @param schoolId
 * @param id
 * @returns null
 * @throws {import('./http').ApiError} 409 if the section has in-flight passes
 */
export function deleteClassSection(schoolId: Id, id: Id): Promise<null> {
  return schoolsApi<null>(`/api/schools/${schoolId}/class-sections/${id}`, {
    method: 'DELETE',
  });
}

/**
 * A non-owner TEACHER caller gets 404, not 403, so this also 404s for a
 * teacher not assigned to the section — not distinguishable client-side from
 * "section doesn't exist". Anyone below TEACHER is blocked earlier by
 * requireMinRole and gets 403.
 *
 * @param schoolId
 * @param id
 */
export function getRoster(
  schoolId: Id,
  id: Id
): Promise<ClassSectionRosterResponse> {
  return schoolsApi<ClassSectionRosterResponse>(
    `/api/schools/${schoolId}/class-sections/${id}/roster`
  );
}

/**
 * Replace a class section's roster (full replace — the body IS the new
 * roster; an empty array clears it). Pass exactly one of `studentIds` or
 * `studentEmails`; a teacher building their own roster generally has emails.
 *
 * A student already in a DIFFERENT section for this same period is moved out
 * of it. For a non-admin caller that requires a confirmation round trip: the
 * first call 409s with a `ClassSectionRosterConflictResponse` body (read it
 * off `ApiError.body`) listing each student and the teacher who currently
 * holds them, and writes nothing. Resubmit the identical body with
 * `confirmReassign: true` to go through; the 200 then reports what actually
 * moved in `reassigned`. ADMIN+ callers never see the 409.
 *
 * @param schoolId
 * @param id
 * @param body
 * @throws {import('./http').ApiError} 409 with a conflict list (non-admin, unconfirmed); 422 if an email/id doesn't resolve to a STUDENT in this school, or the move exceeds the non-admin reassignment limit; 404 if the section doesn't exist or isn't the caller's
 */
export function putRoster(
  schoolId: Id,
  id: Id,
  body: PutClassSectionRosterBody
): Promise<ClassSectionRosterResponse> {
  return schoolsApi<ClassSectionRosterResponse>(
    `/api/schools/${schoolId}/class-sections/${id}/roster`,
    { method: 'PUT', body }
  );
}

/**
 * Sync sections and rosters from a full period-by-period desired state in
 * one call (weekly re-runnable — full-replace per referenced period).
 *
 * `schoolPeriodIds` is the authoritative set of period columns swept by the
 * full-replace delete — every period the caller intends to sync, including
 * all-blank columns with no assignments. `students[].assignments` merely
 * populates them, and every assignment's schoolPeriodId must appear in
 * `schoolPeriodIds` or the call 422s.
 *
 * @param schoolId
 * @param body
 * @throws {import('./http').ApiError} 422 if any period/student/teacher email can't be resolved, or the same student is assigned two different teachers for one period in this payload — all-or-nothing, nothing is changed
 */
export function createClassSectionsBulk(
  schoolId: Id,
  body: BulkClassSectionSyncBody
): Promise<BulkClassSectionSyncResult> {
  return schoolsApi<BulkClassSectionSyncResult>(
    `/api/schools/${schoolId}/class-sections/bulk`,
    {
      method: 'POST',
      body,
    }
  );
}
